import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorageIcon from '@mui/icons-material/Storage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

import { useCart } from 'services/cart/CartContext';

interface CartDebugInfoProps {
  showDetails?: boolean;
}

/**
 * Componente para debug e informações do carrinho
 * Útil para desenvolvimento e suporte
 */
const CartDebugInfo: React.FC<CartDebugInfoProps> = ({
  showDetails = false,
}) => {
  const { cart, isStorageAvailable, storageInfo } = useCart();

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!showDetails) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.7 }}>
        <StorageIcon fontSize="small" />
        <Chip
          size="small"
          icon={isStorageAvailable ? <CheckCircleIcon /> : <ErrorIcon />}
          label={isStorageAvailable ? 'Salvo' : 'Não salvo'}
          color={isStorageAvailable ? 'success' : 'error'}
          variant="outlined"
        />
        <Typography variant="caption" color="text.secondary">
          {formatBytes(storageInfo.size)}
        </Typography>
      </Box>
    );
  }

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StorageIcon />
          <Typography variant="subtitle2">Informações do Carrinho</Typography>
          <Chip
            size="small"
            icon={isStorageAvailable ? <CheckCircleIcon /> : <ErrorIcon />}
            label={
              isStorageAvailable ? 'Persistência Ativa' : 'Sem Persistência'
            }
            color={isStorageAvailable ? 'success' : 'error'}
            variant="outlined"
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Estado Atual:
            </Typography>
            <Typography variant="caption" component="div">
              • Total de itens: {cart.totalItems}
            </Typography>
            <Typography variant="caption" component="div">
              • Valor total: R$ {cart.totalValue.toFixed(2)}
            </Typography>
            <Typography variant="caption" component="div">
              • Valor assinante: R$ {cart.subscriberTotalValue.toFixed(2)}
            </Typography>
            <Typography variant="caption" component="div">
              • Economia: R${' '}
              {(cart.totalValue - cart.subscriberTotalValue).toFixed(2)}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Armazenamento:
            </Typography>
            <Typography variant="caption" component="div">
              • Disponível: {isStorageAvailable ? 'Sim' : 'Não'}
            </Typography>
            <Typography variant="caption" component="div">
              • Tamanho: {formatBytes(storageInfo.size)}
            </Typography>
            <Typography variant="caption" component="div">
              • Chave: clube42-cart
            </Typography>
          </Box>

          {cart.items.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Itens no Carrinho:
              </Typography>
              {cart.items.map((item, index) => (
                <Typography key={item.id} variant="caption" component="div">
                  {index + 1}. {item.beer.name} (x{item.quantity})
                </Typography>
              ))}
            </Box>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary">
              O carrinho é salvo automaticamente no navegador e persistirá por
              até 7 dias. Os itens são validados automaticamente para garantir
              disponibilidade.
            </Typography>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default CartDebugInfo;
